import React, { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, CandlestickSeries, LineSeries } from 'lightweight-charts';
import { TrendingUp, TrendingDown, Maximize2, Minimize2 } from 'lucide-react';

const StockChart = ({ candleData, analysis, symbol }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const macdSeriesRef = useRef(null);
  const sma50SeriesRef = useRef(null);
  const sma100SeriesRef = useRef(null);
  const sma200SeriesRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Calculate SMA (Simple Moving Average)
  const calculateSMA = (data, period) => {
    if (!data || data.length < period) return [];
    
    const smaData = [];
    const closes = data.map(c => parseFloat(c.c || c.close || 0)).filter(c => c > 0);
    
    for (let i = period - 1; i < closes.length; i++) {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) {
        sum += closes[j];
      }
      const sma = sum / period;
      if (!isNaN(sma) && isFinite(sma)) {
        smaData.push({
          time: data[i].t ? Math.floor(data[i].t / 1000) : Math.floor(Date.now() / 1000),
          value: sma
        });
      }
    }
    
    return smaData;
  };

  // Calculate MACD line (main MACD line, not signal/histogram)
  const calculateMACD = (data, fastPeriod = 12, slowPeriod = 26) => {
    if (!data || data.length < slowPeriod) return [];
    
    const closes = data.map(c => parseFloat(c.c || c.close || 0)).filter(c => c > 0);
    
    // Calculate EMA
    const calculateEMA = (values, period) => {
      const multiplier = 2 / (period + 1);
      let ema = values.slice(0, period).reduce((a, b) => a + b, 0) / period;
      const emaData = [];
      
      for (let i = 0; i < period - 1; i++) {
        emaData.push(null); // No EMA for first period-1 values
      }
      
      for (let i = period - 1; i < values.length; i++) {
        ema = (values[i] - ema) * multiplier + ema;
        if (!isNaN(ema) && isFinite(ema)) {
          emaData.push(ema);
        } else {
          emaData.push(null);
        }
      }
      
      return emaData;
    };
    
    const fastEMA = calculateEMA(closes, fastPeriod);
    const slowEMA = calculateEMA(closes, slowPeriod);
    
    // Calculate MACD line (fast EMA - slow EMA)
    const macdData = [];
    const startIndex = slowPeriod - 1; // Start from where slow EMA begins
    
    for (let i = 0; i < data.length; i++) {
      if (i >= startIndex && fastEMA[i] !== null && slowEMA[i] !== null) {
        const macd = fastEMA[i] - slowEMA[i];
        if (!isNaN(macd) && isFinite(macd)) {
          macdData.push({
            time: data[i].t ? Math.floor(data[i].t / 1000) : Math.floor(Date.now() / 1000),
            value: macd
          });
        }
      }
    }
    
    return macdData;
  };

  useEffect(() => {
    if (!chartContainerRef.current || !candleData || candleData.length === 0) return;

    const container = chartContainerRef.current;
    let resizeObserver = null;
    let isInitialized = false;
    let timeoutId = null;
    
    // Ensure container has proper dimensions - this is critical for chart initialization
    // Use ResizeObserver to wait for container to be properly sized
    const initializeChart = () => {
      // Check if already initialized
      if (isInitialized) return;
      
      // Check if container is actually in the DOM and visible
      if (!container || !container.isConnected) {
        return;
      }
      
      const rect = container.getBoundingClientRect();
      const containerWidth = container.clientWidth || container.offsetWidth || rect.width;
      const containerHeight = container.clientHeight || container.offsetHeight || rect.height || (isFullscreen ? window.innerHeight - 100 : 500);
      
      // If container has no width or isn't visible, wait for ResizeObserver
      if (!containerWidth || containerWidth === 0 || rect.width === 0) {
        return;
      }
      
      // Mark as initialized to prevent double initialization
      isInitialized = true;

      // Cleanup previous chart
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        macdSeriesRef.current = null;
        sma50SeriesRef.current = null;
        sma100SeriesRef.current = null;
        sma200SeriesRef.current = null;
      }

      // Ensure minimum dimensions (chart library requires at least 1px)
      const finalWidth = Math.max(containerWidth, 100);
      const finalHeight = Math.max(containerHeight, 100);

      // Create chart with explicit dimensions
      // The chart needs explicit width/height, not autoSize
      const chart = createChart(container, {
        width: finalWidth,
        height: finalHeight,
        layout: {
          backgroundColor: '#ffffff',
          textColor: '#333',
        },
        grid: {
          vertLines: {
            color: '#f0f0f0',
          },
          horzLines: {
            color: '#f0f0f0',
          },
        },
        crosshair: {
          mode: 1, // Normal mode
        },
        rightPriceScale: {
          borderColor: '#cccccc',
        },
        timeScale: {
          borderColor: '#cccccc',
          timeVisible: true,
          secondsVisible: false,
        },
      });
      
      // Force chart to resize to ensure it's properly initialized
      chart.resize(finalWidth, finalHeight);

      // Verify chart was created successfully
      if (!chart) {
        console.error('Failed to create chart instance');
        return;
      }

      chartRef.current = chart;

      // Use a longer delay to ensure chart is fully initialized
      // The chart needs to complete its internal setup and render before we can add series
      timeoutId = setTimeout(() => {
        try {
          // Verify chart still exists and container is still valid
          if (!chartRef.current || !chartContainerRef.current || !container.isConnected) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Chart or container no longer valid - skipping series creation');
            }
            isInitialized = false;
            return;
          }

          // Double-check chart is ready
          if (!chart || typeof chart.addSeries !== 'function') {
            console.error('Chart not ready - addSeries not available');
            isInitialized = false;
            return;
          }

          // Verify container still has dimensions
          const rect = container.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0) {
            console.error('Container has no dimensions:', rect);
            isInitialized = false;
            return;
          }

          // Add candlestick series (v5.1 API - use imported series type)
          const candlestickSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#26a69a',
            downColor: '#ef5350',
            borderVisible: false,
            wickUpColor: '#26a69a',
            wickDownColor: '#ef5350',
          });

          if (!candlestickSeries) {
            console.error('Failed to create candlestick series');
            isInitialized = false;
            return;
          }

          candlestickSeriesRef.current = candlestickSeries;

          // Format candle data - ensure sorted and unique timestamps
          const formattedData = candleData
            .map((candle) => ({
              time: candle.t ? Math.floor(candle.t / 1000) : Math.floor(Date.now() / 1000),
              open: parseFloat(candle.o || candle.open || 0),
              high: parseFloat(candle.h || candle.high || 0),
              low: parseFloat(candle.l || candle.low || 0),
              close: parseFloat(candle.c || candle.close || 0),
            }))
            .filter(c => c.open > 0 && c.high > 0 && c.low > 0 && c.close > 0)
            .sort((a, b) => a.time - b.time) // Sort by time ascending
            .filter((item, index, arr) => {
              // Remove duplicate timestamps
              return index === 0 || item.time !== arr[index - 1].time;
            });

          if (formattedData.length > 0) {
            candlestickSeries.setData(formattedData);
          }

          // Calculate and add MACD line
          const macdData = calculateMACD(candleData);
          if (macdData.length > 0) {
            try {
              const macdSeries = chart.addSeries(LineSeries, {
                color: '#9C27B0',
                lineWidth: 2,
                title: 'MACD',
                priceScaleId: 'macd',
              });
              macdSeriesRef.current = macdSeries;
              
              // Create separate price scale for MACD (can be negative)
              chart.priceScale('macd').applyOptions({
                scaleMargins: {
                  top: 0.1,
                  bottom: 0.1,
                },
              });
              
              macdSeries.setData(macdData);
            } catch (macdError) {
              console.warn('Error adding MACD series:', macdError);
            }
          }

          // Calculate and add SMA 50, 100, 200
          const sma50Data = calculateSMA(candleData, 50);
          const sma100Data = calculateSMA(candleData, 100);
          const sma200Data = calculateSMA(candleData, 200);

          if (sma50Data.length > 0) {
            try {
              const sma50Series = chart.addSeries(LineSeries, {
                color: '#2196F3',
                lineWidth: 2,
                title: 'SMA 50',
                priceLineVisible: false,
              });
              sma50SeriesRef.current = sma50Series;
              sma50Series.setData(sma50Data);
            } catch (sma50Error) {
              console.warn('Error adding SMA 50 series:', sma50Error);
            }
          }

          if (sma100Data.length > 0) {
            try {
              const sma100Series = chart.addSeries(LineSeries, {
                color: '#FF9800',
                lineWidth: 2,
                title: 'SMA 100',
                priceLineVisible: false,
              });
              sma100SeriesRef.current = sma100Series;
              sma100Series.setData(sma100Data);
            } catch (sma100Error) {
              console.warn('Error adding SMA 100 series:', sma100Error);
            }
          }

          if (sma200Data.length > 0) {
            try {
              const sma200Series = chart.addSeries(LineSeries, {
                color: '#4CAF50',
                lineWidth: 2,
                title: 'SMA 200',
                priceLineVisible: false,
              });
              sma200SeriesRef.current = sma200Series;
              sma200Series.setData(sma200Data);
            } catch (sma200Error) {
              console.warn('Error adding SMA 200 series:', sma200Error);
            }
          }

          // Add support/resistance lines if analysis available
          if (analysis && analysis.currentPrice) {
            const currentPrice = analysis.currentPrice;
            
            // Add current price line
            candlestickSeries.createPriceLine({
              price: currentPrice,
              color: '#2196F3',
              lineWidth: 2,
              lineStyle: 2, // Dashed
              axisLabelVisible: true,
              title: 'Current Price',
            });

            // Add entry/exit points if available
            if (analysis.entryPrice) {
              candlestickSeries.createPriceLine({
                price: analysis.entryPrice,
                color: '#4CAF50',
                lineWidth: 2,
                lineStyle: 1, // Solid
                axisLabelVisible: true,
                title: 'Entry',
              });
            }

            if (analysis.stopLoss) {
              candlestickSeries.createPriceLine({
                price: analysis.stopLoss,
                color: '#F44336',
                lineWidth: 2,
                lineStyle: 1,
                axisLabelVisible: true,
                title: 'Stop Loss',
              });
            }

            if (analysis.targetPrice) {
              candlestickSeries.createPriceLine({
                price: analysis.targetPrice,
                color: '#FF9800',
                lineWidth: 2,
                lineStyle: 1,
                axisLabelVisible: true,
                title: 'Target',
              });
            }
          }

          // Fit content
          chart.timeScale().fitContent();
        } catch (seriesError) {
          console.error('Error adding candlestick series:', seriesError);
          console.error('Chart state:', { 
            chart: !!chart, 
            container: container.isConnected, 
            width: container.clientWidth,
            height: container.clientHeight,
            rect: container.getBoundingClientRect()
          });
          isInitialized = false; // Reset flag on error
        }
      }, 200); // 200ms delay to ensure chart is fully rendered
    };

    // Try to initialize immediately
    initializeChart();
    
    // If not ready, use ResizeObserver to wait for container to be sized
    if (!isInitialized && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 0 && !isInitialized) {
            initializeChart();
            if (resizeObserver) {
              resizeObserver.disconnect();
              resizeObserver = null;
            }
            break;
          }
        }
      });
      resizeObserver.observe(container);
    } else if (!isInitialized) {
      // Fallback: use requestAnimationFrame if ResizeObserver not available
      const checkReady = () => {
        if (!isInitialized) {
          initializeChart();
          if (!isInitialized) {
            requestAnimationFrame(checkReady);
          }
        }
      };
      requestAnimationFrame(checkReady);
    }

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        const newWidth = chartContainerRef.current.clientWidth;
        const newHeight = isFullscreen ? window.innerHeight - 100 : 500;
        if (newWidth > 0) {
          chartRef.current.applyOptions({
            width: newWidth,
            height: newHeight,
          });
        }
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      // Clear any pending timeouts
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Reset initialization flag
      isInitialized = false;
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (e) {
          // Chart already removed or invalid
        }
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        macdSeriesRef.current = null;
        sma50SeriesRef.current = null;
        sma100SeriesRef.current = null;
        sma200SeriesRef.current = null;
      }
    };
  }, [candleData, analysis, symbol, isFullscreen]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!candleData || candleData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <p className="text-gray-600">No chart data available</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${isFullscreen ? 'fixed inset-0 z-50 p-4' : 'p-6'}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{symbol} Price Chart</h3>
          {analysis && (
            <p className="text-sm text-gray-600">
              {candleData.length} candles • Last: ${analysis.currentPrice?.toFixed(2) || 'N/A'}
            </p>
          )}
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center">
              <span className="w-3 h-0.5 bg-purple-500 mr-1"></span>
              MACD
            </span>
            <span className="flex items-center">
              <span className="w-3 h-0.5 bg-blue-500 mr-1"></span>
              SMA 50
            </span>
            <span className="flex items-center">
              <span className="w-3 h-0.5 bg-orange-500 mr-1"></span>
              SMA 100
            </span>
            <span className="flex items-center">
              <span className="w-3 h-0.5 bg-green-500 mr-1"></span>
              SMA 200
            </span>
          </div>
        </div>
        <button
          onClick={toggleFullscreen}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
        </button>
      </div>
      <div
        ref={chartContainerRef}
        className="w-full"
        style={{ height: isFullscreen ? 'calc(100vh - 120px)' : '500px' }}
      />
    </div>
  );
};

export default StockChart;

